import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES, ITEMS_PER_PAGE } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} SentNotificationItem
 * @property {number} batch_id - Batch ID for the notification group
 * @property {string} title - Notification title
 * @property {string} description - Notification description
 * @property {{url: string, name: string, extension: string}[]|null} media - Attached media files
 * @property {string|null} href - Link URL
 * @property {*} data - Additional data
 * @property {string} created_at - Created timestamp
 * @property {number} total_recipients - Total number of recipients
 * @property {string} total_read - Total read count
 * @property {string} total_unread - Total unread count
 * @property {string} total_dismissed - Total dismissed count
 * @property {string} total_users - Total user recipients
 * @property {string} total_students - Total student recipients
 * @property {string} receiver_names - Comma-separated receiver names
 * @property {string[]} receiver_roles - Array of receiver roles
 */

/**
 * @typedef {import("../../../common/types/types").PaginationMeta} PaginationMeta
 */

/**
 * @typedef {object} GetSentNotificationParams
 * @property {number} [page] - Page number for pagination
 * @property {number} [per_page] - Number of items per page
 * @property {number} [batch_id] - Filter by batch ID
 * @property {string} [title] - Filter by title
 */

/**
 * Custom hook to fetch sent notification list with pagination
 */
export const useGetSentNotificationList = () => {
    const [notifications, setNotifications] = useState(
        /** @type {SentNotificationItem[] | null | undefined} */(undefined),
    );
    const [meta, setMeta] = useState(
        /** @type {PaginationMeta | null | undefined} */(undefined),
    );
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const { axiosInstance } = useAxios();

    /**
     * Fetch sent notifications with pagination
     * @async
     * @param {GetSentNotificationParams} params
     * @returns {Promise<void>}
     */
    const getSentNotifications = async ({
        page = 1,
        per_page = ITEMS_PER_PAGE,
        batch_id,
        title,
    } = {}) => {
        try {
            setLoading(true);
            setError("");

            const query = new URLSearchParams();
            query.append("all_info", "1");
            query.append("page", page);
            query.append("per_page", per_page);

            if (batch_id) {
                query.append("filter[batch_id]", batch_id);
            }
            if (title) {
                query.append("filter[title]", title);
            }

            const url = `${TENANT_DOMAIN}/announcements/sent?${query.toString()}`;

            const response = await axiosInstance.get(url);

            setNotifications(response.data?.data || []);
            setMeta(response.data?.meta || null);
        } catch (err) {
            setNotifications(null);
            setMeta(null);
            if (err?.response?.status === 500) {
                setError(ERROR_MESSAGES.INTERNAL_SERVER);
            } else {
                setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
            }
        } finally {
            setLoading(false);
        }
    };

    return {
        notifications,
        meta,
        loading,
        error,
        getSentNotifications,
    };
};